'use client'

import PropTypes from 'prop-types'
import { TextEntry } from '@repo/ui/TextEntry'
import { useMattersStore } from '@repo/store/useMattersStore'
import { useLookupsStore } from '@repo/store/useLookupsStore'
import { useEffect, useState } from 'react'
import { useDebounce } from '@uidotdev/usehooks'
import MUIStack from '@mui/material/Stack'
import MUIIconButton from '@mui/material/IconButton'
import { EditIcon } from '@repo/ui/EditIcon'
import { confirm } from '@repo/ui/Confirm'

export const NewUFNEntry = ({
  onChange,
  onError,
  date,
  size = 'small',
  sx = {},
  testMode = false,
  clientNo = null
}) => {
  const { fetchNextUFN, checkIfUFNExists } = useMattersStore()
  const { fetchUserCanOverrideUFN, userCanOverrideUFN } = useLookupsStore()
  const [ufn, setUfn] = useState('')
  const [error, setError] = useState(null)
  const debouncedUfn = useDebounce(ufn, 300)
  const [editMode, setEditMode] = useState(false)

  useEffect(() => {
    if (userCanOverrideUFN === null && !testMode) {
      fetchUserCanOverrideUFN()
    }
  }, [userCanOverrideUFN, fetchUserCanOverrideUFN, testMode])

  useEffect(() => {
    setUfn('')
    setError('')

    if (!date || testMode) {
      return
    }

    const getNextClientNo = async () => {
      const ufn = await fetchNextUFN({ dateTime: date })
      setUfn(ufn)
    }

    getNextClientNo()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [date, userCanOverrideUFN])

  useEffect(() => {
    if (!userCanOverrideUFN || !debouncedUfn || !date) return

    const checkIfUniqueFileNoIsUnique = async () => {
      const exists = await checkIfUFNExists({ ufn: debouncedUfn, clientNo })
      const errorMessage = exists ? 'Unique file no already exists' : null
      setError(errorMessage)
      if (onError) onError(errorMessage)
    }

    checkIfUniqueFileNoIsUnique()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedUfn, date, userCanOverrideUFN])

  const confirmEditMode = async () => {
    if (!editMode) {
      const confirmed = await confirm('Are you sure you want to edit the Unique File Number manually?')
      if (confirmed) setEditMode(true)
    } else {
      setEditMode(false)
    }
  }

  const handleChange = (newValue) => {
    if (userCanOverrideUFN) setUfn(newValue)
  }

  useEffect(() => {
    if (onChange) {
      onChange(ufn)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ufn])

  return (
    <MUIStack direction="row" sx={{ ...sx }} alignItems="center" gap={2}>
      <TextEntry
        data-testid="NewUFNEntry"
        name="uniqueFileNo"
        onChange={handleChange}
        value={ufn}
        disabled={userCanOverrideUFN && !date}
        label="Unique file no"
        readOnly={!editMode}
        errors={error ? { uniqueFileNo: error } : null}
        size={size}
        sx={{ width: userCanOverrideUFN ? 'calc(100% - 48px)' : 'calc(100% - 56px)' }}
      />
      {userCanOverrideUFN ? (
        <MUIIconButton sx={{ width: '48px', height: '48px' }} onClick={confirmEditMode}>
          <EditIcon sx={{ width: '24px', height: '24px' }} />
        </MUIIconButton>
      ) : null}
    </MUIStack>
  )
}

NewUFNEntry.propTypes = {
  onChange: PropTypes.func,
  onError: PropTypes.func,
  date: PropTypes.any,
  size: PropTypes.string,
  sx: PropTypes.object,
  testMode: PropTypes.bool,
  clientNo: PropTypes.string
}
